import { CHAIN_COLORS } from "@/lib/api";
import { Slider } from "@/components/ui/slider";
import { Button } from "@/components/ui/button";
import { Gear, Prohibit, ArrowClockwise, Sliders, ChartLine } from "@phosphor-icons/react";

function StatRow({ label, value }) {
  return (
    <div className="flex items-center justify-between text-xs">
      <span className="text-[#a1a1aa]">{label}</span>
      <span className="font-mono text-[#f4f4f5]">{value}</span>
    </div>
  );
}

export default function LeftPanel({ chains, selected, toggleChain, config, onFilterChange,
  onOpenSettings, onOpenBlacklist, onRefresh, refreshing, onOpenPnl, solStatus }) {
  return (
    <aside className="col-span-12 lg:col-span-2 border-r border-[#27272a] overflow-y-auto p-4 flex flex-col gap-6" data-testid="left-panel">
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-head text-xs uppercase tracking-widest text-[#a1a1aa]">Chains</h3>
        </div>
        <div className="flex flex-col gap-1.5">
          {(chains || []).map((c) => {
            const active = selected.includes(c.name);
            const color = CHAIN_COLORS[c.name] || "#06b6d4";
            return (
              <button key={c.name} data-testid={`chain-toggle-${c.name.toLowerCase()}`}
                onClick={() => toggleChain(c.name)}
                className={`flex items-center justify-between px-2.5 py-2 border text-xs transition-colors ${active ? "border-[#3f3f46] bg-[#18181b]" : "border-transparent bg-transparent hover:bg-[#18181b]"}`}>
                <span className="flex items-center gap-2">
                  <span className="h-2 w-2 rounded-full" style={{ backgroundColor: active ? color : "#3f3f46" }} />
                  <span className={active ? "text-[#f4f4f5]" : "text-[#a1a1aa]"}>{c.name}</span>
                </span>
                {!c.live_data && <span className="text-[9px] font-mono text-[#836EF9]">SIM</span>}
              </button>
            );
          })}
        </div>
      </div>

      <div>
        <h3 className="font-head text-xs uppercase tracking-widest text-[#a1a1aa] mb-3">Screener Filters</h3>
        <div className="flex flex-col gap-5">
          <div>
            <div className="flex justify-between mb-2 text-xs">
              <span className="text-[#a1a1aa]">Min Market Cap</span>
              <span className="font-mono text-cyan-400">${(config.min_market_cap / 1000).toFixed(0)}K</span>
            </div>
            <Slider data-testid="filter-mcap" min={0} max={5000000} step={100000}
              value={[config.min_market_cap]} onValueChange={(v) => onFilterChange("min_market_cap", v[0])} />
          </div>
          <div>
            <div className="flex justify-between mb-2 text-xs">
              <span className="text-[#a1a1aa]">Liquidity Floor</span>
              <span className="font-mono text-cyan-400">${(config.liquidity_floor / 1000).toFixed(0)}K</span>
            </div>
            <Slider data-testid="filter-liquidity" min={0} max={500000} step={5000}
              value={[config.liquidity_floor]} onValueChange={(v) => onFilterChange("liquidity_floor", v[0])} />
          </div>
          <div>
            <div className="flex justify-between mb-2 text-xs">
              <span className="text-[#a1a1aa]">Min Confidence</span>
              <span className="font-mono text-cyan-400">{config.min_confidence}</span>
            </div>
            <Slider data-testid="filter-confidence" min={0} max={100} step={5}
              value={[config.min_confidence]} onValueChange={(v) => onFilterChange("min_confidence", v[0])} />
          </div>
        </div>
      </div>

      <div className="border border-[#27272a] p-3 flex flex-col gap-2">
        <h3 className="font-head text-xs uppercase tracking-widest text-[#a1a1aa] mb-1">Risk Engine</h3>
        <StatRow label="Risk / Trade" value={`$${config.risk_per_trade}`} />
        <StatRow label="Daily Max Loss" value={`$${config.daily_max_loss}`} />
        <StatRow label="TP Target" value={`$${config.tp_target_usd}`} />
        <StatRow label="Stop Loss" value={`$${config.stop_loss_usd}`} />
        <StatRow label="Max Hold" value={`${config.max_hold_min}m`} />
      </div>

      <div className="border border-[#27272a] p-3 flex flex-col gap-1.5">
        <div className="flex items-center justify-between">
          <h3 className="font-head text-xs uppercase tracking-widest text-[#a1a1aa]">Live Wallet</h3>
          <span className={`h-2 w-2 rounded-full ${solStatus?.configured ? "bg-green-500" : "bg-zinc-600"}`} data-testid="sol-wallet-dot" />
        </div>
        <p className="text-[10px] font-mono text-[#a1a1aa]" data-testid="sol-wallet-status">
          {solStatus?.configured
            ? `Solana · ${(solStatus.wallet_address || "").slice(0, 6)}…`
            : "Not configured (paper only)"}
        </p>
        {solStatus?.sol_balance != null && (
          <p className="text-[10px] font-mono text-cyan-400">{solStatus.sol_balance.toFixed(3)} SOL</p>
        )}
      </div>

      <div className="flex flex-col gap-2 mt-auto">
        <Button data-testid="open-pnl-btn" onClick={onOpenPnl} variant="outline"
          className="rounded-none border-[#27272a] bg-transparent hover:bg-[#18181b] text-xs justify-start">
          <ChartLine size={14} className="mr-2" /> Performance
        </Button>
        <Button data-testid="refresh-btn" onClick={onRefresh} disabled={refreshing} variant="outline"
          className="rounded-none border-[#27272a] bg-transparent hover:bg-[#18181b] text-xs justify-start">
          <ArrowClockwise size={14} className={`mr-2 ${refreshing ? "animate-spin" : ""}`} /> Refresh Signals
        </Button>
        <Button data-testid="open-settings-btn" onClick={onOpenSettings} variant="outline"
          className="rounded-none border-[#27272a] bg-transparent hover:bg-[#18181b] text-xs justify-start">
          <Sliders size={14} className="mr-2" /> Risk Settings
        </Button>
        <Button data-testid="open-blacklist-btn" onClick={onOpenBlacklist} variant="outline"
          className="rounded-none border-[#27272a] bg-transparent hover:bg-[#18181b] text-xs justify-start">
          <Prohibit size={14} className="mr-2" /> Blacklist
        </Button>
      </div>
    </aside>
  );
}
